import { OrdinaryObject } from "src/modules/utils/shared/ordinary-object.model";
import { Utils } from "src/modules/utils/shared/utils";
import { TableCellType } from "./table-cell-type.enum";
import { TableColumn } from "./table-column.model";
import { TableRow } from "./table-row.model";
import { TableSortColumn } from "./table-sort-column.model";

export class TableData<T = any> {

    static fromList(data: OrdinaryObject[], cols?: string[], unroll?: boolean): TableData {
        if (cols == null) {
            cols = Utils.arrayGetUnique(Utils.arrayExplode(data, item => Utils.getOwnPropertyNames(item)));
        }
        return new TableData(cols.map(col => new TableColumn(col, col)), data.map(item => {
            let value = item;
            if (unroll) {
                value = {};
                for (let col of cols) {
                    value[col] = Utils.getPropertyNested(item, col);
                }
            }
            return new TableRow("", item, value);
        }));
    }

    static update(data: TableData, columns: TableColumn[], rows: TableRow[], sort: TableSortColumn[] = null, generator: () => Promise<TableRow> = null): TableData {
        if (data != null) {
            data.rows = rows;
            data.useRowIds = rows?.length > 0 && !Utils.isNoe(rows[0].id);
            return data;
        }
        else {
            return new TableData(columns, rows, sort, { generator });
        }
    }

    columns: TableColumn[];
    useRowIds = false;
    generator: () => Promise<TableRow> = null;

    constructor(columns: TableColumn[], public rows: TableRow<T>[], public sort: TableSortColumn[] = null, props?: Partial<TableData>) {
        this.columns = columns != null ? Utils.arrayWithoutNull(columns) : null;
        this.useRowIds = rows?.length > 0 && !Utils.isNoe(rows[0].id);
        if (props != null) {
            Object.assign(this, props);
        } 
    }

    toCsvCell(cell: string): string {
        if (cell.includes(";") || cell.includes("\"")) {
            return "\"" + Utils.replaceAll(cell, "\"", "\\\"") + "\"";
        }
        else {
            return cell;
        }
    }

    toCsv(rows: TableRow[] = null): string {
        let cols = this.columns.filter(col => col.cellType != TableCellType.button);
        let result = Utils.arrayItemsToString(cols, ";", col => this.toCsvCell(col.getFullCaption()));
        for (let row of rows ?? this.rows) {
            try {
                for (let col of cols) {
                    if (!Utils.hasProperty(row.values, col.id)) {
                        throw new Error("Das Objekt enthält nicht das Feld " + col.id);
                    }
                }
                result += "\r\n" + Utils.arrayItemsToString(cols, ";", col => this.toCsvCell(col.toCsvString(row.values[col.id])));
            }
            catch (error: any) {
                throw new Error("Konnte Zeile nicht kopieren. " + Utils.toJson(row.values, true) + ". Columns: " + Utils.arrayItemsToString(cols.map(col => col.id)) + " Fehlermeldung: " + error);
            }
        }
        return result;
    }

    toAsciiTable(rows: TableRow[] = null): string {
        let cols = this.columns.filter(col => col.cellType != TableCellType.button, { autoWidth: 1 });
    
        let colWidths = cols.map(col => {
            let maxWidth = col.getFullCaption().length;
            for (let row of rows ?? this.rows) {
                let cellValue = col.toCsvString(row.values[col.id] ?? "");
                maxWidth = Math.max(maxWidth, cellValue.length);
            }
            return maxWidth;
        });
    
        const formatRow = (row: string[]) => '| ' + row.map((cell, i) => cell.padEnd(colWidths[i], ' ')).join(' | ') + ' |';
        const drawLine = () => '+-' + colWidths.map(width => '-'.repeat(width)).join('-+-') + '-+';
    
        let result = drawLine() + '\n' + formatRow(cols.map(col => col.getFullCaption())) + '\n' + drawLine() + '\n';
    
        for (let row of rows ?? this.rows) {
            try {
                result += formatRow(cols.map(col => col.toCsvString(row.values[col.id] ?? ''))) + '\n';
            } 
            catch (error: any) {
                throw new Error("Konnte Zeile nicht kopieren. " + Utils.toJson(row.values, true) + ". Columns: " + Utils.arrayItemsToString(cols.map(col => col.id)) + " Fehlermeldung: " + error);
            }
        }    
        result += drawLine();
    
        return result;
    }

    toId(): string {
        return Utils.arrayItemsToString(this.columns, ", ", col => col.id);
    }

}
